import { IColumn, IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import { _columns } from 'common/components/ActionMenu/Attach/_columns.data';
import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import { useStyles } from 'common/components/ActionMenu/Attach/AttachFormModal/index.styles';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import { DocumentViewModalState } from 'common/components/DocumentList';
import { TABLE_ROWS } from 'common/constants';
import {
  AttachableTravelerDocuments,
  AttachableTravelerDocuments_attachableTravelerDocuments_nodes,
} from 'common/graphql/__generated__/AttachableTravelerDocuments';
import { AttachTravelerDocumentVariables } from 'common/graphql/__generated__/AttachTravelerDocument';
import { TravelerAvailableDocumentTypes } from 'common/graphql/__generated__/TravelerAvailableDocumentTypes';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import React, { useCallback } from 'react';
import { TravelerDetails_traveler } from '../../../../__generated__/TravelerDetails';
import { toOrderByVariable } from '../../utils';

import ATTACHABLE_TRAVELER_DOCUMENTS from 'common/graphql/AttachableTravelerDocuments.graphql';
import ATTACH_TRAVELER_DOCUMENTS from 'common/graphql/AttachTravelerDocument.graphql';
import TRAVELER_DETAILS from '../../../../TravelerDetails.graphql';

type AttachableDataType =
  AttachableTravelerDocuments_attachableTravelerDocuments_nodes;
interface AttachFormModalProps {
  availableDocumentTypes: TravelerAvailableDocumentTypes | undefined;
  traveler: TravelerDetails_traveler;
  isDirty: boolean;
}

export const AttachFormModal: React.FC<AttachFormModalProps> = ({
  availableDocumentTypes,
  traveler,
}) => {
  const styles = useStyles();
  const { id, isDraft } = { ...traveler };

  const getDocumentTypeOptions = useCallback(
    (data: typeof availableDocumentTypes | undefined) => {
      return (
        data?.travelerAvailableDocumentTypes?.nodes.map((doctype) => ({
          key: doctype.id,
          text: doctype.documentType || '',
          title:
            !isDraft && doctype.isAccountingDocument
              ? 'Upload prohibited during/after the approval process'
              : undefined,
          disabled: !isDraft && doctype.isAccountingDocument ? true : undefined,
        })) || []
      );
    },
    [isDraft]
  );

  const canSelectItem = useCallback(
    (item: AttachableDataType) =>
      isDraft ? true : !item._isAccountingDocument!,
    [isDraft]
  );

  const transformData = useCallback(
    (data: AttachableTravelerDocuments | undefined) => {
      return data?.attachableTravelerDocuments?.nodes || [];
    },
    []
  );

  const getMutationInput = useCallback(
    (selectedItems: AttachableDataType[], entityId: string) => {
      const entityDocIds = selectedItems.map((item) => item.id);
      return {
        input: {
          travelerId: entityId,
          entityDocumentId: entityDocIds,
        },
      } as AttachTravelerDocumentVariables;
    },
    []
  );

  const onRenderItemColumn = useCallback(
    (
      item?: AttachableDataType,
      _index?: number,
      column?: IColumn,
      openDocumentViewModal?: (state: DocumentViewModalState) => void
    ) => {
      if (!item || !column) return undefined;

      const disableLogic = isDraft ? false : item._isAccountingDocument;

      const fieldContent = item[
        column.fieldName as keyof AttachableDataType
      ] as string | null;
      switch (column.key) {
        case 'fileReference':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <DocumentDataCallout disabled={disableLogic!} item={item} />
            </Stack>
          );
        case 'documentType':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item.documentTypes?.documentType}
              </Text>
            </Stack>
          );
        case 'indexAmount':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <Text className={styles.contentColumnAlignRight}>
                {fieldContent}
              </Text>
            </Stack>
          );
        case 'isoCode':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item.currency?.isoCode}
              </Text>
            </Stack>
          );
        case '_uploadDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item._uploadDate ? getGlobalDateFormat(item._uploadDate) : ''}
              </Text>
            </Stack>
          );
        case 'indexTransactionDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              {item.indexTransactionDate && (
                <Text className={disableLogic ? styles.disabledText : ''}>
                  {dateFormat(dateConvertions(item.indexTransactionDate))}
                </Text>
              )}
            </Stack>
          );
        case 'view':
          const viewDocumentVisible =
            item._isProtected! || item._fileViewer !== 'browser';
          return (
            <Stack
              className={styles.columnHeight}
              tokens={{ childrenGap: 10 }}
              horizontal
              verticalAlign="center"
            >
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentVisible}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    openDocumentViewModal?.({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );
        default:
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {fieldContent}
              </Text>
            </Stack>
          );
      }
    },
    [styles, isDraft]
  );

  return (
    <GenericAttachForm
      entity={{
        id,
        isDraft,
      }}
      availableDocumentTypes={availableDocumentTypes}
      attachableDocumentsQuery={ATTACHABLE_TRAVELER_DOCUMENTS}
      attachDocumentsMutation={ATTACH_TRAVELER_DOCUMENTS}
      detailsQuery={TRAVELER_DETAILS}
      columns={_columns}
      modalWidth={1350}
      getDocumentTypeOptions={getDocumentTypeOptions}
      transformData={transformData}
      canSelectItem={canSelectItem}
      getMutationInput={getMutationInput}
      onRenderItemColumn={onRenderItemColumn}
      initialQueryVariables={{
        travelerId: id,
        first: TABLE_ROWS,
      }}
      toOrderByVariable={toOrderByVariable}
    />
  );
};
